import {useEffect, useRef, useState} from "react";
import add_above from "../../assets/add_row_above.svg"
import add_below from "../../assets/add_row_below.svg"
import delete_row from "../../assets/delete.svg"
import valuer_api from "../../api/valuer";


const ValuerAmendExpandableTableContainer = (props) => {

    // For Saving
    const summaryId = props.summaryId
    const section = props.sectionName
    const identifier = props.identifier

    // Required
    // const columns = props.columns
    const [localColumns, setLocalColumns] = useState([...props.columns]);

    const [isFreshInsert, setIsFreshInsert] = useState(props.isFreshInsert ?? false)    // Tracks if it is first render or not
    const [freshRowOrders, setFreshRowOrders] = useState(new Set()) // Tracking if we have a new row insert to conditionally allow blank rows
    const [freshColumnIndexes, setFreshColumnIndexes] = useState(new Set()) // Same logic as for the rows

    const hasCommittedInitialTitle = useRef(false); // Used to ensure we are allowing black titles but still updating nested structure


    // Value
    const [values, setValues] = useState([])

    const [hasTitle, setHasTitle] = useState(false)
    // Saving Tracker
    const UpdateParentValue = props.UpdateParentValue
    const AddToQueue = props.AddToQueue
    const RemoveFromQueue = props.RemoveFromQueue
    const AddToErroredSaves = props.AddToErroredSaves

    // Error Handling
    const setErrorMessage = props.setErrorMessage
    const setErrorTitle = props.setErrorTitle

    const [showExpandMenu, setShowExpandMenu] = useState(false)
    const [expandMenuLocation, setExpandMenuLocation] = useState({"x": null, "y": null})
    const [showVerticalMenu, setShowVerticalMenu] = useState(false)
    const [showHorizontalMenu, setShowHorizontalMenu] = useState(false)
    const [menuLeft, setMenuLeft] = useState(0)
    const [menuTop, setMenuTop] = useState(0)

    // parse "col-12" -> 12
    const colNum = (id) => parseInt(String(id).replace('col-', ''), 10);
    const colId  = (n)  => `col-${n}`;

    useEffect(() => {
        setLocalColumns([...props.columns]);
    }, [props.columns]);

    useEffect(() => {
        if (props.titleValue && props.titleValue.trim() !== "") {
            setHasTitle(true);
            hasCommittedInitialTitle.current = true; // prevent overwrite fallback
        }
    }, [props.titleValue]);

    const displayValues = (() => {
        const filtered = isFreshInsert
            ? values
            : values.filter(row =>
                freshRowOrders.has(row.order) ||
                localColumns.some(col => (row[col.identifier] ?? '').trim?.() !== '')
            );
        return filtered.length > 0 ? filtered : values.slice(0, 1); // ensure at least one row is shown
    })();


    const displaylocalColumns = (() => {
        const filtered = isFreshInsert
            ? localColumns
            : localColumns.filter((col, idx) =>
                freshColumnIndexes.has(idx) ||
                displayValues.some(row => (row[col.identifier] ?? '').trim?.() !== '')
            );
        return filtered.length > 0 ? filtered : localColumns.slice(0, 1); // ensure at least one column is shown
    })();




    useEffect(() => {
        const initialValues = Array.isArray(props.values)
            ? props.values
            : Object.values(props.values ?? {});
        setValues(initialValues);
    }, [props.values]);



    const getNewRow = () => {
        let new_row = {}
        for (let x = 0; x < localColumns.length; x++) {
            if (localColumns[x].identifier && localColumns[x].default) new_row[localColumns[x].identifier] = localColumns[x].default
            else if (localColumns[x].identifier) new_row[localColumns[x].identifier] = ""
        }
        return new_row
    }

    const DeleteRow = () => {
        const idx = (expandMenuLocation?.y ?? 0)
        const updateTime = Date.now();

        // 1. Identify the row being removed
        const deletedRow = values[idx];

        // 2. Create a new copy without the deleted row
        const v_copy = values
            .filter((_, i) => i !== idx)
            .map((row, i) => ({ ...row, order: i + 1 }));

        // 3. Identify the highest order in the original set
        const lastRow = values[values.length - 1];

        // 4. Build a cleanup row to overwrite the old trailing one
        const blankLastRow = {};
        for (const col of localColumns) {
            blankLastRow[col.identifier] = "";
        }
        blankLastRow["order"] = null; // optional: can also use values.length or delete entirely
        blankLastRow["row"] = deletedRow?.row ?? deletedRow?.order ?? null; // preserve row ID if relevant
        blankLastRow["nested"] = 1;

        const v_with_blank = [...v_copy, blankLastRow];

        setValues(v_copy);
        UpdateParentValue(identifier, v_copy);

        const cleanForSave = (arr) =>
            arr.map(row => {
                const cleaned = {};
                for (const key in row) {
                    if (!['row', 'nested', 'order'].includes(key)) {
                        cleaned[key] = row[key];
                    }
                }
                cleaned.order = row.order;
                return cleaned;
            });

        const body = { value: cleanForSave(v_with_blank) };

        AddToQueue(section, identifier, updateTime);

        valuer_api.ValuerAmendPut(summaryId, section, identifier, body)
            .then(() => RemoveFromQueue(section, identifier, updateTime))
            .catch(() => {
                AddToErroredSaves(section, identifier, updateTime);
                setErrorTitle("Failed to Save");
                setErrorMessage("The request to save a field within the table has failed");
            });

        setShowVerticalMenu(false);
        setShowHorizontalMenu(false);
    };



    const InsertRow = (add_below = false) => {
        let idx = (expandMenuLocation?.y ?? 0)
        if (add_below) idx += 1
        const updateTime = Date.now()

        const newRow = getNewRow()
        const _new_vs = [
            ...values.slice(0, idx),
            newRow,
            ...values.slice(idx),
        ].map((row, i) => ({ ...row, order: i + 1 }));

        const newRowOrder = idx + 1; // new inserted row’s order


        setValues(_new_vs)
        setFreshRowOrders(prev => new Set(prev).add(newRowOrder));
        UpdateParentValue(identifier, _new_vs)

        const body = { value: _new_vs }
        AddToQueue(section, identifier, updateTime)
        valuer_api.ValuerAmendPut(summaryId, section, identifier, body)
            .then(() => RemoveFromQueue(section, identifier, updateTime))
            .catch(() => {
                AddToErroredSaves(section, identifier, updateTime)
                setErrorTitle("Failed to Save")
                setErrorMessage("The request to save a field within the table has failed")
            })

        setShowVerticalMenu(false)
        setShowHorizontalMenu(false)
    }


    const InsertColumn = (add_right = false) => {
        const updateTime = Date.now();

        // 1) figure out the *1-based* insertion index
        let new_col_idx = (expandMenuLocation?.x ?? 0) + 1;
        if (add_right) new_col_idx += 1;

        // 2) update rows (shift right, then insert blank at new_col_idx)
        const v_copy = [...values].map(row => ({ ...row }));
        // shift from right to left to avoid overwriting
        for (const row of v_copy) {
            // find current max column index
            const maxIdx = Math.max(
                0,
                ...Object.keys(row)
                    .filter(k => k.startsWith('col-'))
                    .map(colNum)
            );
            for (let i = maxIdx; i >= new_col_idx; i--) {
                row[colId(i + 1)] = row[colId(i)];
            }
            row[colId(new_col_idx)] = ''; // new blank cell
        }

        // 3) update localColumns (shift right, insert new col meta)
        const updatedCols = [...localColumns];
        for (let i = updatedCols.length - 1; i >= new_col_idx - 1; i--) {
            updatedCols[i + 1] = {
                ...updatedCols[i],
                identifier: colId(i + 2),
            };
        }
        updatedCols[new_col_idx - 1] = { identifier: colId(new_col_idx), default: '' };

        setLocalColumns(updatedCols);
        setFreshColumnIndexes(prev => new Set(prev).add(new_col_idx - 1));
        setValues(v_copy);                 // <<<<< IMPORTANT
        UpdateParentValue(identifier, v_copy);

        const body = { value: v_copy };
        AddToQueue(section, identifier, updateTime);
        valuer_api.ValuerAmendPut(summaryId, section, identifier, body)
            .then(() => RemoveFromQueue(section, identifier, updateTime))
            .catch(() => {
                AddToErroredSaves(section, identifier, updateTime);
                setErrorTitle('Failed to Save');
                setErrorMessage('The request to save a field within the table has failed');
            });

        setShowVerticalMenu(false);
        setShowHorizontalMenu(false);
    };

    const DeleteColumn = () => {
        const updateTime = Date.now();
        const del_idx = (expandMenuLocation?.x ?? 0) + 1; // 1-based index

        // 1) update rows: remove that column and shift left
        const v_copy = [...values].map(row => ({ ...row }));
        for (const row of v_copy) {
            const maxIdx = Math.max(
                0,
                ...Object.keys(row)
                    .filter(k => k.startsWith('col-'))
                    .map(colNum)
            );

            // shift left starting at del_idx
            for (let i = del_idx; i <= maxIdx; i++) {
                row[colId(i)] = row[colId(i + 1)] ?? '';
            }
            delete row[colId(maxIdx + 1)]; // clean trailing
        }

        // 2) update localColumns: remove meta and renumber
        const updatedCols = [...localColumns];
        updatedCols.splice(del_idx - 1, 1);
        for (let i = 0; i < updatedCols.length; i++) {
            updatedCols[i] = { ...updatedCols[i], identifier: colId(i + 1) };
        }
        setLocalColumns(updatedCols);

        setValues(v_copy);                 // <<<<< IMPORTANT
        UpdateParentValue(identifier, v_copy);

        const body = { value: v_copy };
        AddToQueue(section, identifier, updateTime);
        valuer_api.ValuerAmendPut(summaryId, section, identifier, body)
            .then(() => RemoveFromQueue(section, identifier, updateTime))
            .catch(() => {
                AddToErroredSaves(section, identifier, updateTime);
                setErrorTitle('Failed to Save');
                setErrorMessage('The request to save a field within the table has failed');
            });

        setShowVerticalMenu(false);
        setShowHorizontalMenu(false);
    };

    const ClickOff = () => {
        if (!identifier || !section || !summaryId) return
        SaveAll()
    }

    const SaveAll = () => {
        const updateTime = Date.now()

        // Ensure proper ordering
        const v_copy = values.map((row, i) => ({
            ...row,
            order: i + 1,
        }))

        const body = { value: v_copy }

        setValues(v_copy)
        UpdateParentValue(identifier, v_copy)
        AddToQueue(section, identifier, updateTime)
        setFreshRowOrders(new Set());
        setFreshColumnIndexes(new Set());


        valuer_api.ValuerAmendPut(summaryId, section, identifier, body)
            .then(() => {
                RemoveFromQueue(section, identifier, updateTime)
            })
            .catch(() => {
                AddToErroredSaves(section, identifier, updateTime)
                setErrorTitle("Failed to Save")
                setErrorMessage("The request to save a field within the table has failed")
            })
    }


    const handleExpandMenu = (ev, x, displayY, actualY = null) => {
        const displayRow = displayValues.sort((a, b) => a.order - b.order)[displayY];
        const actualIndex = values.findIndex(v => v.order === displayRow?.order);

        if (!displayRow || actualIndex === -1) return; // early exit if broken

        // Allow always re-triggering if it's the only cell or first hover
        const isSingleCell =
            displayValues.length === 1 && displaylocalColumns.length === 1;

        const isAlreadyFocused =
            expandMenuLocation.x === x && expandMenuLocation.y === actualIndex;

        if (isAlreadyFocused && !isSingleCell) return;


        if (expandMenuLocation.x !== x || expandMenuLocation.y !== actualIndex) {
            setShowHorizontalMenu(false);
            setShowVerticalMenu(false);
        }

        setShowExpandMenu(true);
        setExpandMenuLocation({ x, y: actualY, displayY });
    };


    const handleExpandButtonClick = (ev, direction) => {
        let elem = document.getElementById(`cell-${expandMenuLocation.x}-${expandMenuLocation.y}`).getBoundingClientRect()
        let scrollHeight = document.body.scrollTop

        if (direction === "horizontal") {
            setMenuTop(elem.top + scrollHeight - (elem.height / 2) - 16)
            setMenuLeft(elem.left + (elem.width / 2) + 19)
            setShowVerticalMenu(false)
            setShowHorizontalMenu(true)
        } else {
            setMenuTop(elem.top + scrollHeight - 16)
            setMenuLeft(elem.left + 16)
            setShowHorizontalMenu(false)
            setShowVerticalMenu(true)
        }
    }

    const filteredRowsWithIndex = displayValues
        .sort((a, b) => a.order - b.order)
        .map(row => {
            const actualIndex = values.findIndex(v => v.order === row.order);
            return {
                row,
                actualIndex: actualIndex !== -1 ? actualIndex : 0  // fallback
            }
        })


    return (
        <div className="valuer-amend-table-field-container">
            <div className="valuer-amend-table" style={{width: "unset"}} onMouseLeave={() => setShowExpandMenu(false)}>

                {filteredRowsWithIndex.map(({ row, actualIndex: y_idx }, displayYIdx) => {

                    return (
                        <>
                            <div className={"valuer-amend-table-row-container" + (y_idx === Object.keys(values).length - 1 ? ' bottom' : '')}
                                 style={{width: "100%"}}>
                                {displaylocalColumns && displaylocalColumns.map((column, x_idx) => {
                                    return (
                                        <div className={"valuer-amend-table-field-column-container" + (x_idx === 0 ? ' left' : '')}
                                             style={{width: `${100 / (displaylocalColumns?.length || 1)}%`}}
                                             id={`cell-${x_idx}-${y_idx}`} // still use actual index for ID
                                             onMouseOver={ev => handleExpandMenu(ev, x_idx, displayYIdx, y_idx)}

                                        >
                                            <div className="valuer-amend-table-free-text-container">
                                                <input
                                                    type="text"
                                                    value={row?.[column.identifier] ?? ''}
                                                    onChange={ev => {
                                                        const updated = [...values]
                                                        updated[y_idx][column.identifier] = ev.target.value

                                                        setValues(updated)

                                                    }}
                                                    onBlur={() => {
                                                        ClickOff();
                                                        const val = row?.[column.identifier] ?? '';
                                                        const isMeaningful = val.trim?.() !== '';
                                                        const isTableField = identifier.includes("-table-") && !identifier.includes("-title-");

                                                        if (isMeaningful && isTableField && !hasCommittedInitialTitle.current && !hasTitle) {
                                                            hasCommittedInitialTitle.current = true;
                                                            props.triggerInitialTitleInsert?.();
                                                        }
                                                    }}


                                                />

                                            </div>
                                            {showExpandMenu && expandMenuLocation.x === x_idx && expandMenuLocation.displayY === displayYIdx ?
                                                <>
                                                    <div className="table-expand-button horizontal"
                                                         onClick={ev => handleExpandButtonClick(ev, 'horizontal')}>
                                                        <div className="table-expand-button-dot"/>
                                                        <div className="table-expand-button-dot"/>
                                                        <div className="table-expand-button-dot"/>
                                                        <div className="table-expand-button-dot"/>
                                                        <div className="table-expand-button-dot"/>
                                                        <div className="table-expand-button-dot"/>
                                                    </div>
                                                    <div className="table-expand-button vertical"
                                                         onClick={ev => handleExpandButtonClick(ev, 'vertical')}>
                                                        <div className="table-expand-button-dot"/>
                                                        <div className="table-expand-button-dot"/>
                                                        <div className="table-expand-button-dot"/>
                                                        <div className="table-expand-button-dot"/>
                                                        <div className="table-expand-button-dot"/>
                                                        <div className="table-expand-button-dot"/>
                                                    </div>
                                                </> : null
                                            }
                                        </div>
                                    )
                                })}
                            </div>

                        </>
                    )
                })}
                {showVerticalMenu ?
                    <div className="valuer-amend-table-row-options-container"
                         style={{display: "block", left: menuLeft, top: menuTop, margin: "unset"}}
                         onMouseLeave={() => setShowVerticalMenu(false)}>
                        <div className="valuer-amend-table-row-button-container add link"
                             onClick={() => InsertRow()}>
                            <div className="valuer-amend-table-row-button-img-container">
                                <img src={add_above} alt="Add Above Row"/>
                            </div>
                            <div className="valuer-amend-table-row-button-text-container">
                                Add Row Above
                            </div>
                        </div>
                        <div className="valuer-amend-table-row-button-container delete link"
                             onClick={() => DeleteRow()}>
                            <div className="valuer-amend-table-row-button-img-container">
                                <img src={delete_row} alt="Delete Row"/>
                            </div>
                            <div className="valuer-amend-table-row-button-text-container">
                                Delete Row
                            </div>
                        </div>
                        <div className="valuer-amend-table-row-button-container add link"
                             onClick={() => InsertRow(true)}>
                            <div className="valuer-amend-table-row-button-img-container">
                                <img src={add_below} alt="Add Below Row"/>
                            </div>
                            <div className="valuer-amend-table-row-button-text-container">
                                Add Row Below
                            </div>
                        </div>
                    </div> : null
                }
                {showHorizontalMenu ?
                    <div className="valuer-amend-table-row-options-container"
                         style={{display: "block", left: menuLeft, top: menuTop, margin: "unset"}}
                         onMouseLeave={() => setShowHorizontalMenu(false)}>
                        <div className="valuer-amend-table-row-button-container add link"
                             onClick={() => InsertColumn()}>
                            <div className="valuer-amend-table-row-button-img-container">
                                <img src={add_above} alt="Add Above Row" style={{rotate: "270deg"}}/>
                            </div>
                            <div className="valuer-amend-table-row-button-text-container">
                                Add Column Left
                            </div>
                        </div>
                        <div className="valuer-amend-table-row-button-container delete link"
                             onClick={() => DeleteColumn()}>
                            <div className="valuer-amend-table-row-button-img-container">
                                <img src={delete_row} alt="Delete Row"/>
                            </div>
                            <div className="valuer-amend-table-row-button-text-container">
                                Delete Column
                            </div>
                        </div>
                        <div className="valuer-amend-table-row-button-container add link"
                             onClick={() => InsertColumn(true)}>
                            <div className="valuer-amend-table-row-button-img-container">
                                <img src={add_below} alt="Add Below Row" style={{rotate: "270deg"}}/>
                            </div>
                            <div className="valuer-amend-table-row-button-text-container">
                                Add Column Right
                            </div>
                        </div>
                    </div> : null
                }
            </div>
        </div>
    )
}



export default ValuerAmendExpandableTableContainer